import React from 'react';
import { BrowserRouter, Routes, Route } from 'react-router-dom';
import { createTheme, ThemeProvider } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import './App.css';
import { UserAuthContextProvider } from './context/UserAuthContext';
import { CategoryContextProvider } from './context/CategoryContext';
import { SearchProvider } from './context/SearchContext';
import ProtectedRoute from './components/ProtectedRoute';
import Home from './views/Home/Home';
import Form from './views/Form/Form';
import UploadHTML from './views/UploadHTML/UploadHTML';
import Login from './views/Login/Login';
import ChangeStore from './views/ChangeStore/ChangeStore';
import PricingComp from './views/PricingComp/PricingComp';
import FacilitiesWalkthrough from './views/FacilitiesWalkthrough/components/FacilitiesWalkthroughNew';
import FacilitiesReview from './views/FacilitiesReview/FacilitiesReview';
import FacilitiesReviewWalkthrough from './views/FacilitiesReview/FacilitiesReviewWalkthrough';
import FacilitiesScores from './views/FacilitiesScores/FacilitiesScores';
import EditProfile from './views/EditProfile/EditProfile';
import Subcategory from './views/Subcategory/Subcategory';
import OloInvoiceAdjustment from './views/OLOInvoiceAdjustment/OLOInvoiceAdjustment';
import OLOLookup from './views/OLOLookup/OLOLookup';
import OLOCoreLookup from './views/OLOCoreLookup/OLOCoreLookup';
import CouponDashboard from './views/OLOCoreLookup/CouponDashboard';
import CouponManagement from './views/CouponManagement/CouponManagement';
import ClientReporting from './views/ClientReporting/ClientReporting';
import DataIntegrity from './views/DataIntegrity/DataIntegrity';
import ViewOLOInvoice from './views/ViewOLOInvoice/ViewOLOInvoice';
import HouseAccountUsers from './views/HouseAccountUsers/HouseAccountUsers';
import HerdServeMapper from './views/HerdServeMapper/QrCodeMapper';
import GLExport from './views/GLExport/GLExport';
import DataIntegrity2 from './views/DataIntegrity2/DataIntegrity2';
import EcoTrakDashboard from './views/EcoTrak/EcoTrakDashboard';
import NPSDashboard from './views/NPS/NPSDashboard';
import NPSCateringDashboard from './views/NPS/NPSCateringDashboard';
import NPSEmployeeDashboard from './views/NPS/NPSEmployeeDashboard';
import SparkflyMemberOffers from './views/Sparkfly/SparkflyMemberOffers';
import RushUsageDashboard from './views/RushUsage/RushUsageDashboard';
import ForecastUploadPage from './views/ForecastUpload/ForecastUploadPage';
import EzCaterDashboard from './views/EzCater/EzCaterDashboard';
import Redirect from './Redirect';

// Create a compact theme with smaller fonts and spacing
const theme = createTheme({
  typography: {
    fontSize: 13, // Default is 14
    htmlFontSize: 16,
    h1: { fontSize: '2rem' },
    h2: { fontSize: '1.75rem' },
    h3: { fontSize: '1.5rem' },
    h4: { fontSize: '1.25rem' },
    h5: { fontSize: '1.1rem' },
    h6: { fontSize: '1rem' },
    body1: { fontSize: '0.875rem' },
    body2: { fontSize: '0.8rem' },
    caption: { fontSize: '0.7rem' },
    button: { fontSize: '0.8rem' },
  },
  components: {
    MuiButton: {
      defaultProps: {
        size: 'small',
      },
      styleOverrides: {
        root: {
          textTransform: 'none',
        },
      },
    },
    MuiTextField: {
      defaultProps: {
        size: 'small',
      },
    },
    MuiSelect: {
      defaultProps: {
        size: 'small',
      },
    },
    MuiFormControl: {
      defaultProps: {
        size: 'small',
      },
    },
    MuiTable: {
      defaultProps: {
        size: 'small',
      },
    },
    MuiTableCell: {
      styleOverrides: {
        root: {
          padding: '6px 12px',
          fontSize: '0.8rem',
        },
        head: {
          fontSize: '0.8rem',
          fontWeight: 600,
        },
      },
    },
    MuiIconButton: {
      defaultProps: {
        size: 'small',
      },
    },
    MuiChip: {
      defaultProps: {
        size: 'small',
      },
    },
    MuiAlert: {
      styleOverrides: {
        root: {
          fontSize: '0.8rem',
        },
      },
    },
    MuiDialogTitle: {
      styleOverrides: {
        root: {
          fontSize: '1.1rem',
        },
      },
    },
    MuiInputBase: {
      styleOverrides: {
        root: {
          fontSize: '0.85rem',
        },
      },
    },
    MuiInputLabel: {
      styleOverrides: {
        root: {
          fontSize: '0.85rem',
        },
      },
    },
    MuiMenuItem: {
      styleOverrides: {
        root: {
          fontSize: '0.85rem',
        },
      },
    },
  },
});

function App() {
  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <UserAuthContextProvider>
        <CategoryContextProvider>
          <SearchProvider>
            <BrowserRouter>
              <Routes>
              <Route
                exact
                path='/home'
                element={
                  <ProtectedRoute>
                    <Home />
                  </ProtectedRoute>
                }
              />
              <Route
                exact
                path='/olo-invoice-adjustment'
                element={
                  <ProtectedRoute>
                    <OloInvoiceAdjustment />
                  </ProtectedRoute>
                }
              />
              <Route
                exact
                path='/olo-house-account-users'
                element={
                  <ProtectedRoute>
                    <HouseAccountUsers />
                  </ProtectedRoute>
                }
              />
              <Route
                exact
                path='/olo-invoice/:exportBatchId/:oloInvoiceNumber'
                element={
                  <ProtectedRoute>
                    <ViewOLOInvoice />
                  </ProtectedRoute>
                }
              />
              <Route
                exact
                path='/offer-code-mgmt'
                element={
                  <ProtectedRoute>
                    <CouponManagement />
                  </ProtectedRoute>
                }
              />
              <Route
                exact
                path='/olo-lookup'
                element={
                  <ProtectedRoute>
                    <OLOLookup />
                  </ProtectedRoute>
                }
              />
              <Route
                exact
                path='/olo-core-lookup'
                element={
                  <ProtectedRoute>
                    <OLOCoreLookup />
                  </ProtectedRoute>
                }
              />
              <Route
                exact
                path='/olo-core-lookup/coupons'
                element={
                  <ProtectedRoute>
                    <CouponDashboard />
                  </ProtectedRoute>
                }
              />
              <Route
                exact
                path='/client-reporting'
                element={
                  <ProtectedRoute>
                    <ClientReporting />
                  </ProtectedRoute>
                }
              />
              <Route
                exact
                path='/data-integrity'
                element={
                  <ProtectedRoute>
                    <DataIntegrity />
                  </ProtectedRoute>
                }
              />
              <Route
                exact
                path='/change-store'
                element={
                  <ProtectedRoute>
                    <ChangeStore />
                  </ProtectedRoute>
                }
              />
              <Route
                exact
                path='/facilities-walkthrough'
                element={
                  <ProtectedRoute>
                    <FacilitiesWalkthrough />
                  </ProtectedRoute>
                }
              />
              <Route
                exact
                path='/facilities-review'
                element={
                  <ProtectedRoute>
                    <FacilitiesReview />
                  </ProtectedRoute>
                }
              />
              <Route
                exact
                path='/facilities-review/walkthrough/:fwtId'
                element={
                  <ProtectedRoute>
                    <FacilitiesReviewWalkthrough />
                  </ProtectedRoute>
                }
              />
              <Route
                exact
                path='/facilities-scores'
                element={
                  <ProtectedRoute>
                    <FacilitiesScores />
                  </ProtectedRoute>
                }
              />
              <Route
                exact
                path='/category/:categoryId'
                element={
                  <ProtectedRoute>
                    <Subcategory />
                  </ProtectedRoute>
                }
              />
              <Route
                exact
                path='/category/:categoryId/form/:jotformId'
                element={
                  <ProtectedRoute>
                    <Form />
                  </ProtectedRoute>
                }
              />
              <Route
                exact
                path='/category/:categoryId/subcategory/:subcategoryId/form/:jotformId'
                element={
                  <ProtectedRoute>
                    <Form />
                  </ProtectedRoute>
                }
              />
              <Route
                exact
                path='/upload-html'
                element={
                  <ProtectedRoute>
                    <UploadHTML />
                  </ProtectedRoute>
                }
              />
              <Route
                exact
                path='/pricing-comp'
                element={
                  <ProtectedRoute>
                    <PricingComp />
                  </ProtectedRoute>
                }
              />
              <Route
                exact
                path='/profile'
                element={
                  <ProtectedRoute>
                    <EditProfile />
                  </ProtectedRoute>
                }
              />
              <Route
                exact
                path='/herd-serve-mapper'
                element={
                  <ProtectedRoute>
                    <HerdServeMapper />
                  </ProtectedRoute>
                }
              />
              <Route
                exact
                path='/gl-export'
                element={
                  <ProtectedRoute>
                    <GLExport />
                  </ProtectedRoute>
                }
              />
              <Route
                exact
                path='/data-integrity-2'
                element={
                  <ProtectedRoute>
                    <DataIntegrity2 />
                  </ProtectedRoute>
                }
              />
              <Route
                exact
                path='/ecotrak'
                element={
                  <ProtectedRoute>
                    <EcoTrakDashboard />
                  </ProtectedRoute>
                }
              />
              <Route
                exact
                path='/nps'
                element={
                  <ProtectedRoute>
                    <NPSDashboard />
                  </ProtectedRoute>
                }
              />
              <Route
                exact
                path='/nps-catering'
                element={
                  <ProtectedRoute>
                    <NPSCateringDashboard />
                  </ProtectedRoute>
                }
              />
              <Route
                exact
                path='/nps-employee'
                element={
                  <ProtectedRoute>
                    <NPSEmployeeDashboard />
                  </ProtectedRoute>
                }
              />
              <Route
                exact
                path='/sparkfly-member-offers'
                element={
                  <ProtectedRoute>
                    <SparkflyMemberOffers />
                  </ProtectedRoute>
                }
              />
              <Route
                exact
                path='/rush-usage'
                element={
                  <ProtectedRoute>
                    <RushUsageDashboard />
                  </ProtectedRoute>
                }
              />
              <Route
                exact
                path='/forecast-upload'
                element={
                  <ProtectedRoute>
                    <ForecastUploadPage />
                  </ProtectedRoute>
                }
              />
              <Route
                exact
                path='/ezcater'
                element={
                  <ProtectedRoute>
                    <EzCaterDashboard />
                  </ProtectedRoute>
                }
              />
                <Route path='/redirect' element={<Redirect />} />
                <Route path='/' element={<Login />} />
              </Routes>
            </BrowserRouter>
          </SearchProvider>
        </CategoryContextProvider>
      </UserAuthContextProvider>
    </ThemeProvider>
  );
}

export default App;
